/**
 * Hono → legacy snake_case piece-shape adapters.
 *
 * S-072 / FEAT-006 (Phase 9). The PieceView refactor swaps the data
 * source from Directus to Hono /pieces, but the right-rail panels,
 * RightRail container, and PieceBody consume snake_case `PieceDetail`
 * / `PieceDraft` / `PieceReport` / `PieceSystemDoc` / `PieceImage`
 * types from `@/types/piece`.
 *
 * Rather than rewrite every panel + supporting component to consume
 * the camelCase Hono shape directly, we adapt at the PieceView edge:
 * Hono camelCase → snake_case. The adapter is a single conversion
 * pass per fetch; the downstream components do not change.
 *
 * Adapter scope:
 *   - HonoPiece             → PieceDetail
 *   - HonoPieceDraft        → PieceDraft
 *   - HonoPieceReport       → PieceReport
 *   - HonoPieceSystemDoc    → PieceSystemDoc
 *   - HonoPieceImage        → PieceImage
 *
 * Each adapter is `O(1)` per row — they do nothing more than rename and
 * default-fill missing fields. They never reach back to the network.
 *
 * Fields not surfaced by GET /pieces (e.g. publication_date is on the
 * row but not on the list projection) come back from the detail GET
 * /pieces/:id which selects `*`; if a particular field genuinely is
 * not exposed by the API, the adapter substitutes `null` and the
 * panels gracefully render "—".
 */

import type {
  PieceDetail,
  PieceDraft,
  PieceImage,
  PieceReport,
  PieceStatus,
  PieceSystemDoc,
  GenerationPhase,
  UiState,
} from "@/types/piece";

// ---------- Hono row shapes (camelCase from the API) ----------

export interface HonoPiece {
  id: number;
  tenant_id: number;
  agencyclient_id: number;
  publication_id: number;
  slug: string;
  title: string;
  abstract: string | null;
  content_type: string;
  content_format: string;
  content_origin: string;
  language: string;
  // FEAT-015 — per-piece Spanish variant override (null = inherit publication).
  spanish_country_override?: string | null;
  // FEAT-011 / S-181 — editor-set display label.
  category?: string | null;
  tone: string | null;
  target_word_count: number | null;
  editorial_direction: string | null;
  status: string;
  generation_phases: string | null;
  ui_state: string;
  revision_count: number;
  revision_target: string | null;
  revision_notes: string | null;
  score: number | null;
  quality_score: number | null;
  tone_style_score?: number | null;
  voice_notes?: string | null;
  voice_evaluator_notes?: string | null;
  voice_match_score?: number | null;
  rhythm_score?: number | null;
  voice_structure_score?: number | null;
  vocabulary_score?: number | null;
  seo_score: number | null;
  aeo_score: number | null;
  geo_score: number | null;
  focus_keyword: string | null;
  seo_title: string | null;
  meta_description: string | null;
  aeo_required: boolean;
  geo_required: boolean;
  publish_targets?: unknown[] | null;
  publication_date: string | null;
  generate_cover_images?: boolean;
  source_urls?: string | null;
  signature_name: string | null;
  signature_position: string | null;
  social_amplification_status?: string;
  run_job_id?: string | null;
  run_phase?: string | null;
  run_started_at?: string | null;
  run_finished_at?: string | null;
  run_error?: string | null;
  created_at: string;
  updated_at: string;
  deleted_at: string | null;
}

export interface HonoPieceDraft {
  id: number;
  piece_id: number;
  page_type: string;
  title: string;
  content: string;
  version: number;
  created_at: string;
  updated_at: string;
  deleted_at: string | null;
}

export interface HonoPieceReport {
  id: number;
  page_type: string;
  title: string;
  content: string;
  version: number;
  created_at: string;
  updated_at: string;
  deleted_at: string | null;
}

export interface HonoPieceSystemDoc {
  id: number;
  page_type: string;
  title: string;
  content: string;
  version: number;
  created_at: string;
  updated_at: string;
  deleted_at: string | null;
}

export interface HonoPieceImage {
  id: number;
  piece_id: number;
  name: string;
  format: string;
  style: string;
  prompt: string | null;
  image_file_id: string | null;
  selected_featured: boolean;
  selected_social: boolean;
  source_platform: string;
  attribution_text: string | null;
  attribution_url: string | null;
  created_at: string;
  updated_at: string;
}

export interface HonoListResponse<T> {
  data: T[];
  meta: { total: number; limit: number; offset: number };
}

// ---------- Adapters ----------

export function adaptPiece(p: HonoPiece): PieceDetail {
  return {
    id: p.id,
    slug: p.slug,
    title: p.title,
    abstract: p.abstract,
    status: p.status as PieceStatus,
    generation_phases: (p.generation_phases ?? null) as GenerationPhase,
    ui_state: (p.ui_state ?? null) as UiState | null,
    content_type: p.content_type,
    content_format: p.content_format,
    content_origin: p.content_origin,
    tone: p.tone,
    language: p.language,
    spanish_country_override: p.spanish_country_override ?? null,
    category: p.category ?? null,
    target_word_count: p.target_word_count,
    publication_date: p.publication_date,
    publication_id: p.publication_id,
    // The legacy schema had a `client` integer pointing at client_profiles.
    // In the SaaS model, the workspace = client identity — caller passes the
    // agencyclient id from the JWT scope; we surface the row's
    // agencyclient_id here so any downstream code that branches on `client`
    // still has a value to work with.
    client: p.agencyclient_id,
    date_created: p.created_at,
    date_updated: p.updated_at,
    revision_count: p.revision_count,
    run_phase: p.run_phase ?? null,
    editorial_direction: p.editorial_direction,
    source_urls: p.source_urls ?? null,
    quality_score: p.quality_score,
    tone_style_score: p.tone_style_score ?? null,
    voice_notes: p.voice_notes ?? null,
    voice_evaluator_notes: p.voice_evaluator_notes ?? null,
    voice_match_score: p.voice_match_score ?? null,
    rhythm_score: p.rhythm_score ?? null,
    voice_structure_score: p.voice_structure_score ?? null,
    vocabulary_score: p.vocabulary_score ?? null,
    seo_score: p.seo_score,
    aeo_score: p.aeo_score,
    geo_score: p.geo_score,
    focus_keyword: p.focus_keyword,
    seo_title: p.seo_title,
    meta_description: p.meta_description,
    revision_target: p.revision_target,
    revision_notes: p.revision_notes,
    publish_targets: (p.publish_targets ?? null) as unknown[] | null,
    // No `publication` slug in the SaaS schema — pieces point at
    // `publication_id`. The panel displays the slug for human reference.
    publication: null,
    signature_name: p.signature_name,
    signature_position: p.signature_position,
    aeo_required: p.aeo_required,
    geo_required: p.geo_required,
  };
}

export function adaptDraft(d: HonoPieceDraft): PieceDraft {
  return {
    word_count: (d as { word_count?: number | null }).word_count ?? null,
    id: d.id,
    article: d.piece_id,
    page_type: d.page_type,
    title: d.title,
    content: d.content,
    version: d.version,
    date_created: d.created_at,
  };
}

export function adaptReport(r: HonoPieceReport): PieceReport {
  return {
    id: r.id,
    page_type: r.page_type,
    title: r.title,
    content: r.content,
    version: r.version,
    date_created: r.created_at,
  };
}

export function adaptSystemDoc(d: HonoPieceSystemDoc): PieceSystemDoc {
  return {
    id: d.id,
    page_type: d.page_type,
    title: d.title,
    content: d.content,
    version: d.version,
  };
}

export function adaptImage(i: HonoPieceImage): PieceImage {
  return {
    id: i.id,
    article: i.piece_id,
    name: i.name,
    prompt: i.prompt,
    style: i.style,
    format: i.format,
    selected_featured: i.selected_featured,
    selected_social: i.selected_social,
    // `image` historically carried the Directus asset id. We now hold an
    // R2 file UUID — same shape (string), different host. The asset URL
    // builder (`r2AssetUrl`) takes care of the host swap.
    image: i.image_file_id,
    date_created: i.created_at,
  };
}
